import React from 'react';
import { Control, useController } from 'react-hook-form';
import { Card, CardContent } from '@design-system/Card';
import { Input } from '@/components/design-system/Input';
import { cn } from '@/utils/styles';
import { Range } from '@viome/viome-base/src/usecase/Questionnaire/Questionnaire.model';

interface Props {
  control: Control<Record<string, TimeFieldValue>>;
  name: string;
  subtitle?: string;
  info?: string;
  placeholder?: string;
  required?: boolean;
  ranges?: Range[];
  disabled?: boolean;
}

interface TimeFieldValue {
  hours: number;
  minutes: number;
}

const TimeInputWrapper: React.FC<Props> = ({
  control,
  name,
  subtitle,
  info,
  placeholder = 'Enter time',
  required = false,
  ranges,
  disabled = false,
}) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: control as Control<Record<string, unknown>>,
    rules: {
      required: required ? 'This field is required' : false,
      validate: (value: unknown) => {
        if (!value) return required ? 'This field is required' : true;

        const timeValue = value as TimeFieldValue;
        if (
          !timeValue ||
          typeof timeValue.hours !== 'number' ||
          typeof timeValue.minutes !== 'number'
        ) {
          return 'Invalid input format';
        }

        const hours = timeValue.hours;
        const minutes = timeValue.minutes;

        if (isNaN(hours) || isNaN(minutes)) {
          return 'Please enter valid numbers';
        }

        const hoursRange = ranges?.[0];
        const minutesRange = ranges?.[1];

        if (hoursRange && (hours < hoursRange.min || hours > hoursRange.max)) {
          return `Hours must be between ${hoursRange.min} and ${hoursRange.max}`;
        }

        if (
          minutesRange &&
          (minutes < minutesRange.min || minutes > minutesRange.max)
        ) {
          return `Minutes must be between ${minutesRange.min} and ${minutesRange.max}`;
        }

        return true;
      },
    },
  });

  const handleValueChange =
    (type: 'hours' | 'minutes') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value, 10);
      const currentValue = (field.value as TimeFieldValue) || {
        hours: 0,
        minutes: 0,
      };

      field.onChange({
        ...currentValue,
        [type]: isNaN(value) ? 0 : value,
      });
    };

  const getDisplayValue = (type: 'hours' | 'minutes') => {
    const value = field.value as TimeFieldValue;
    return value && typeof value[type] === 'number'
      ? value[type].toString()
      : '';
  };

  return (
    <div className="w-full">
      <Card className="border-transparent bg-transparent">
        <CardContent className="p-1">
          {subtitle && (
            <p className="typography-body text-secondary mb-4">{subtitle}</p>
          )}
          {info && (
            <p className="typography-body text-secondary mb-6">{info}</p>
          )}
          <div className="flex gap-2 flex-col">
            <div>
              <Input
                id={`${name}-hours`}
                type="number"
                placeholder={`${placeholder} (hours)`}
                onChange={handleValueChange('hours')}
                onBlur={field.onBlur}
                value={getDisplayValue('hours')}
                name={`${field.name}-hours`}
                disabled={disabled}
                className={cn(
                  'h-14 pb-1',
                  disabled && 'opacity-50 cursor-not-allowed',
                )}
                step={ranges?.[0]?.precision || 1}
                min={ranges?.[0]?.min || 0}
                max={ranges?.[0]?.max || 24}
              />
              <span className="text-sm text-gray-500 mt-1 block">
                Hours per day
              </span>
            </div>
            <div>
              <Input
                id={`${name}-minutes`}
                type="number"
                placeholder={`${placeholder} (minutes)`}
                onChange={handleValueChange('minutes')}
                onBlur={field.onBlur}
                value={getDisplayValue('minutes')}
                name={`${field.name}-minutes`}
                disabled={disabled}
                className={cn(
                  'h-14 pb-1',
                  disabled && 'opacity-50 cursor-not-allowed',
                )}
                step={ranges?.[1]?.precision || 1}
                min={ranges?.[1]?.min || 0}
                max={ranges?.[1]?.max || 1440}
              />
              <span className="text-sm text-gray-500 mt-1 block">
                Minutes per day
              </span>
            </div>
          </div>
          {error && <p className="text-red-500 mt-2">{error.message}</p>}
        </CardContent>
      </Card>
    </div>
  );
};

export default TimeInputWrapper;
